import {
	PermissionsMode,
	SiteManagementLanguage,
	SiteManagementSettingsSavingModel
} from "client/Api/SharePointSettings/sharepointSettings";
import { IValueInputAttachable } from "client/Forms/attachable";
import { ReactElement, useMemo } from "react";
import styles from "./SharePointSettingsPage.module.less";
import {
	Checkbox,
	FormItem,
	RadioButtonGroup,
	RadioButtonGroupItem,
	Select,
	SelectItem,
	TextArea,
	ValidationError
} from "client/Components";
import { localize } from "client_legacy/ndaw/culture";

export type SiteManagementTabProps = {
	attachable: IValueInputAttachable<SiteManagementSettingsSavingModel>;
	availableLanguages: readonly SiteManagementLanguage[];
};

export const SiteManagementTab = (props: SiteManagementTabProps): ReactElement => {
	const {
		attachable,
		availableLanguages
	} = props;

	const languageOptions = useMemo(
		(): readonly SelectItem<number>[] => {
			return availableLanguages.map(
				(x): SelectItem<number> => ({
					text: x.name,
					value: x.id
				})
			);
		},
		[availableLanguages]
	);

	return (
		<div className={styles.tabContent}>
			<FormItem>
				<Checkbox
					{...attachable.for("siteManagementEnabled").attachToCheckbox()}
					text={localize("_EnableSiteManagement")}
					ellipseText={false}
				/>
				<ValidationError {...attachable.for("siteManagementEnabled").attachToValidationError()} />
			</FormItem>

			{attachable.for("siteManagementEnabled").value && (
				<>
					<FormItem label={localize("_DefaultDescription")}>
						<TextArea
							{...attachable.for("defaultDescription").attachToTextArea()}
						/>
						<ValidationError {...attachable.for("defaultDescription").attachToValidationError()} />
					</FormItem>

					<FormItem label={localize("_Language")}>
						<Select
							{...attachable.for("language").attachToSelect()}
							options={languageOptions}
							menuTitle={localize("_Language")}
						/>
						<ValidationError {...attachable.for("language").attachToValidationError()} />
					</FormItem>

					<FormItem
						label={localize("_Permissions")}
						isForGroup
					>
						<RadioButtonGroup
							{...attachable.for("permissions").attachToRadioButtonGroup()}
							options={permissionOptions}
						/>
						<ValidationError {...attachable.for("permissions").attachToValidationError()} />
					</FormItem>

					<FormItem
						label={localize("_AllowUserToOverride")}
						isForGroup
					>
						<Checkbox
							{...attachable.for("overrideDescription").attachToCheckbox()}
							text={localize("_Description")}
							ellipseText={false}
						/>

						<Checkbox
							{...attachable.for("overrideLanguage").attachToCheckbox()}
							text={localize("_Language")}
							ellipseText={false}
						/>

						<Checkbox
							{...attachable.for("overridePermissions").attachToCheckbox()}
							text={localize("_Permissions")}
							ellipseText={false}
						/>
					</FormItem>
				</>
			)}
		</div>
	);
};
SiteManagementTab.displayName = "SiteManagementTab";

const permissionOptions: readonly RadioButtonGroupItem<PermissionsMode>[] = [
	{
		text: localize("_UseSamePermissionsAsParentSite"),
		value: PermissionsMode.Inherit
	},
	{
		text: localize("_UseUniquePermissions"),
		value: PermissionsMode.Unique
	}
];
